'use client';

import { useEffect, useRef } from 'react';

const FEATURES = [
  {
    icon: '📍',
    title: 'Hyper-lokale Matches',
    description: 'Wir nutzen deine Postleitzahl, um dir Singles aus deiner direkten Umgebung zu zeigen. Keine 500km-Fernbeziehungen mehr.',
    size: 'md:col-span-2',
    accent: true,
  },
  {
    icon: '🔒',
    title: 'Verifizierte Profile',
    description: 'Jedes Profil wird auf Echtheit geprüft. Du chattest immer mit echten Menschen.',
    size: 'md:col-span-1',
    accent: false,
  },
  {
    icon: '💌',
    title: 'Kostenlose Nachrichten',
    description: 'Schreib die ersten Nachrichten ohne Abonnement. Kein Paywall beim ersten Kontakt.',
    size: 'md:col-span-1',
    accent: false,
  },
  {
    icon: '🎯',
    title: 'Smart Matching',
    description: 'Unser Algorithmus lernt deine Vorlieben kennen und verbessert Matches mit jeder Interaktion.',
    size: 'md:col-span-1',
    accent: false,
  },
  {
    icon: '📱',
    title: 'Mobile First',
    description: 'Optimiert für iOS und Android. Dein Dating-Leben passt in deine Hosentasche.',
    size: 'md:col-span-2',
    accent: false,
  },
];

const TRUST_BADGES = [
  { icon: '🛡️', label: 'DSGVO-konform' },
  { icon: '🔐', label: 'SSL-verschlüsselt' },
  { icon: '✅', label: 'Verifizierte Profile' },
  { icon: '🇩🇪', label: 'Server in Deutschland' },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal, .stagger-children')?.forEach(el => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 md:py-36 px-4 md:px-6"
      style={{ background: 'linear-gradient(180deg, #0C0A0E 0%, #130F18 50%, #0C0A0E 100%)' }}>
      <div className="max-w-7xl mx-auto">

        <div className="reveal mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest px-5 py-2.5 rounded-full"
              style={{ color: '#FF8CA8', background: 'rgba(232,49,90,0.12)', border: '1px solid rgba(232,49,90,0.25)', boxShadow: '0 4px 16px rgba(232,49,90,0.15)' }}>
              Warum xkuss.com
            </span>
            <h2 className="section-headline text-white mt-8">
              Dating, das<br />
              <span className="gradient-text">wirklich funktioniert</span>
            </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed" style={{ color: 'rgba(245,240,247,0.55)' }}>
            Keine Spielchen, kein Paywall-Dschungel. xkuss.com ist gebaut für Menschen, die echte Verbindungen suchen.
          </p>
        </div>

        {/* Asymmetric bento */}
        <div className="stagger-children grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {FEATURES?.map((f, i) => (
            <div key={i}
              className={`feature-card rounded-3xl p-8 md:p-10 relative overflow-hidden ${f?.size}`}
              style={{
                background: f?.accent
                  ? 'linear-gradient(135deg, rgba(232,49,90,0.14) 0%, rgba(22,18,25,0.95) 60%)'
                  : 'var(--gradient-card)',
              }}>
              {f?.accent && (
                <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(232,49,90,0.18) 0%, transparent 70%)' }} />
              )}
              <div className="relative z-10">
                <span className="text-4xl mb-5 block">{f?.icon}</span>
                <h3 className="font-display text-2xl font-bold text-white mb-4">{f?.title}</h3>
                <p className="text-base leading-relaxed" style={{ color: 'rgba(245,240,247,0.6)' }}>
                  {f?.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="reveal flex flex-wrap justify-center gap-4">
          {TRUST_BADGES?.map((b, i) => (
            <div key={i} className="flex items-center gap-2.5 px-6 py-3.5 rounded-full text-sm font-medium transition-all hover:scale-105 hover:border-primary/30"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,247,0.65)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
              <span>{b?.icon}</span>
              {b?.label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}